import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import useAfterMount from '../../hooks/useAfterMount';
import useElementSize from '../../hooks/useElementSize';
import usePrevious from '../../hooks/usePrevious';
const DEFAULT_MIN_WITH = 200;
const DEFAULT_MIN_DAYS = 1;
const DEFAULT_MAX_DAYS = 7;
const ANIMATION_DURATION = 0.2;
const ANIMATION_NEXT = 'page';
const ANIMATION_BACK = 'pageBack';
const ITEM_NAME = 'ColumnItem';
const variants = {
    pageEnter: (pageDirection) => ({
        x: pageDirection === ANIMATION_NEXT ? '60%' : '-60%',
        opacity: 0,
    }),
    pageCenter: () => ({ x: 0, opacity: 1 }),
};
const getDateString = (date) => date.toISOString().split('T').at(0);
const getFirstColumnItem = (node) => {
    if ([...node.classList].includes(ITEM_NAME)) {
        return node;
    }
    return getFirstColumnItem(node.children[0]);
};
const CalendarStripe = forwardRef((props, ref) => {
    const { minDayWith = DEFAULT_MIN_WITH, minDays = DEFAULT_MIN_DAYS, maxDays = DEFAULT_MAX_DAYS, render, renderDay, skipWeekends = false, startDate = new Date(), onNextClick = noop, onPreviousClick = noop, buttonClassName = '', className = '', } = props;
    if (render) {
        console.warn('CalendarStripe has set the "render" prop deprecated. This will be removed in the upcoming releases,' +
            ' Please use the "renderDay" prop instead.');
    }
    if (!render && !renderDay) {
        console.error('CalendarStripe component is missing the required "renderDay" prop');
    }
    const [firstDate, setFirstDate] = useState(startDate);
    const [amountOfDaysToShow, setAmountOfDaysToShow] = useState(1);
    const [enableInitialAnimation, setEnableInitialAnimation] = useState(false);
    // The base for reacting on changing width of the wrapping element.
    // It uses a ResizeObserver under the hood.
    const columnWrapperRef = useRef(null);
    const [columnWrapperWidth] = useElementSize(columnWrapperRef);
    const previous = usePrevious(getDateString(firstDate));
    const animationDirection = getDateString(firstDate) > previous ? ANIMATION_NEXT : ANIMATION_BACK;
    // Update startDate from outside
    const [previousStartDate, setPreviousStartDate] = useState(startDate);
    if (getDateString(startDate) !== getDateString(previousStartDate)) {
        setFirstDate(startDate);
        setPreviousStartDate(startDate);
    }
    useAfterMount(() => {
        setEnableInitialAnimation(true);
    }, []);
    useLayoutEffect(() => {
        if (!columnWrapperRef.current) {
            return;
        }
        const firstItem = getFirstColumnItem(columnWrapperRef.current.children[0]);
        if (!firstItem) {
            return;
        }
        // Get the width of the first column to calculate how many columns fit on one page
        // according to the given minWidth per column
        const columnWidth = firstItem.getBoundingClientRect().width;
        // Limit columns per page for given min and max values
        const allowForLessColumns = amountOfDaysToShow - 1 >= minDays;
        const allowForMoreColumns = amountOfDaysToShow + 1 <= maxDays;
        const goSmaller = allowForLessColumns && columnWidth <= minDayWith;
        const goBigger = allowForMoreColumns && (amountOfDaysToShow + 1) * minDayWith <= columnWrapperWidth;
        if (goBigger) {
            const newValue = amountOfDaysToShow + 1;
            setAmountOfDaysToShow(newValue);
            return;
        }
        if (goSmaller) {
            const newValue = amountOfDaysToShow - 1;
            setAmountOfDaysToShow(newValue);
            return;
        }
    }, [columnWrapperWidth, columnWrapperRef.current, amountOfDaysToShow, firstDate]);
    const handleNext = () => {
        const newFirstDate = new Date(firstDate);
        newFirstDate.setDate(newFirstDate.getDate() + amountOfDaysToShow);
        setFirstDate(newFirstDate);
        onNextClick(newFirstDate);
    };
    const handlePrev = () => {
        const newFirstDate = new Date(firstDate);
        newFirstDate.setDate(newFirstDate.getDate() - amountOfDaysToShow);
        setFirstDate(newFirstDate);
        onPreviousClick(newFirstDate);
    };
    // Re-calculate the dates to show depending on the amount of days to show
    const dates = [];
    let addDays = true;
    let index = 1;
    while (addDays) {
        const currentDate = new Date(firstDate);
        currentDate.setDate(firstDate.getDate() + index);
        const isSaturday = currentDate.getDay() === 6;
        const isSunday = currentDate.getDay() === 0;
        const isWeekend = isSaturday || isSunday;
        if (isWeekend && !skipWeekends) {
            dates.push(currentDate);
        }
        else if (!isWeekend) {
            dates.push(currentDate);
        }
        index++;
        if (dates.length === amountOfDaysToShow) {
            addDays = false;
        }
    }
    const wrapperClassName = classNames('CalenderStripe', 'display-flex', 'align-items-center', 'overflow-hidden', 'bg-white', 'border', 'rounded', className);
    const baseButtonClassName = classNames('align-items-center', 'align-self-stretch', 'display-flex', 'hover-scale-105', 'hover-text-color-darkest', 'padding-10', 'cursor-pointer', 'text-color-darker', 'text-size-12', buttonClassName);
    return (_jsxs("div", Object.assign({ ref: ref, className: wrapperClassName }, { children: [_jsx("div", Object.assign({ className: baseButtonClassName, onClick: handlePrev }, { children: _jsx("span", { className: 'rioglyph rioglyph-chevron-left' }) })), _jsx("div", Object.assign({ className: 'flex-1-1 overflow-hidden', ref: columnWrapperRef }, { children: _jsx(AnimatePresence, Object.assign({ exitBeforeEnter: true, custom: animationDirection }, { children: _jsx(motion.div, Object.assign({ variants: variants, initial: enableInitialAnimation ? 'pageEnter' : false, animate: 'pageCenter', custom: animationDirection, transition: { duration: ANIMATION_DURATION } }, { children: _jsx("div", Object.assign({ className: 'ColumnWrapper display-flex space-x--1 user-select-none' }, { children: dates.map(date => (_jsxs("div", Object.assign({ className: `${ITEM_NAME} flex-1-1` }, { children: [renderDay ? renderDay(date) : null, render && React.cloneElement(_jsx(_Fragment, { children: render }), { date })] }), `${getDateString(date)}`))) })) }), getDateString(firstDate)) })) })), _jsx("div", Object.assign({ className: baseButtonClassName, onClick: handleNext }, { children: _jsx("span", { className: 'rioglyph rioglyph-chevron-right' }) }))] })));
});
export default CalendarStripe;
